import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { Chart } from '@frontend/charts';
import {
  DashboardWidget,
  DashboardWidgetFC,
  TimePeriodListBoxMenu,
  useDashboardWidget,
  useTimePeriodListBoxMenuState,
} from '@frontend/grid-dashboard';
import { useTranslation } from '@frontend/i18n';
import { useLocationFilter } from '@frontend/location-filter';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { ContentLoader, Text } from '@frontend/design-system';
import { useFetchMissedCallTextROI } from '../hooks';

/**
 * @dashboard-widget
 *
 * id: missed-call-text-roi
 * title: Missed Call Text Conversion
 * description: Highlights the ROI of the missed call text.
 * icon: message-small
 */

export const MissedCallTextROIWidget: DashboardWidgetFC = () => {
  const { t } = useTranslation('analytics');
  const { selectedLocationIds, selectedOrgId, getSelectedLocationData } = useAppScopeStore();
  const { startDate, endDate, ...timePeriodMenuState } = useTimePeriodListBoxMenuState('last30days');
  const { filteredLocationIds } = useLocationFilter();
  const { currentSize } = useDashboardWidget();
  const isTiny = currentSize === 'tiny-slim';

  // Org id is not always same as location id for single locations
  // We need to send the location id if it is a single location and org id when it's a multi
  // With the new GLS, we can't select parent but only child locations, so we are good to check the parentId below
  const hasParent = !!getSelectedLocationData()[selectedLocationIds[0]]?.parentID;
  const orgId = hasParent ? selectedOrgId : selectedLocationIds[0];

  const { data, isLoading, total } = useFetchMissedCallTextROI({
    start_date: `${dayjs(startDate).format('YYYY-MM-DD')}T00:00:00.000`,
    end_date: `${dayjs(endDate).format('YYYY-MM-DD')}T23:59:59.999`,
    location_id: filteredLocationIds,
    org_id: orgId,
  });

  return (
    <DashboardWidget css={styles.mainWrapper}>
      <DashboardWidget.Header>
        {!isTiny && <TimePeriodListBoxMenu {...timePeriodMenuState} readonly value='last30days' />}
      </DashboardWidget.Header>
      <DashboardWidget.Content css={styles.contentWrapper}>
        <div>
          <Text css={styles.totalText} weight='bold'>
            {total}
          </Text>
          <Text style={{ color: theme.colors.white }}>{t('Appts Scheduled')}</Text>
        </div>

        {!isTiny && (
          <div style={{ flex: 1 }}>
            <Chart.TinyAreaChart data={data?.map(({ count = 0 }) => count) ?? []} color={theme.colors.white} />
          </div>
        )}
        <ContentLoader show={isLoading} size='small' />
      </DashboardWidget.Content>
    </DashboardWidget>
  );
};

MissedCallTextROIWidget.config = {
  size: {
    large: 'small-narrow',
    medium: 'small-narrow',
    small: 'tiny-slim',
    extraSmall: 'tiny-slim',
  },
  feature: 'messaging',
};

const styles = {
  mainWrapper: css`
    background: linear-gradient(45deg, ${theme.colors.primary40}, #94bfff);

    * {
      color: ${theme.colors.white};
    }
  `,

  contentWrapper: css`
    display: flex;
    gap: ${theme.spacing(6)};

    p {
      line-height: 1;
    }
  `,

  totalText: css`
    color: ${theme.colors.white};
    font-size: ${theme.fontSize(48)};
  `,
};
